import { theme as AntTheme, Typography, Tag } from 'antd';
import { CheckSquareOutlined, CloseSquareOutlined } from '@ant-design/icons';
import { UserDetail } from './types';
import { IOrganizationMemberRole } from '@copilot/data/graphql/_generated';
import { TableModuleTable } from '@copilot/common/components/tables/tableModule/table';
import { TableModuleColumn } from '@copilot/common/components/tables/tableModule/util';
import drawerManager from '@copilot/common/utils/drawerManager';
import Actions from '@copilot/common/actions';
import ActiveStatus from './activeStatus';
import styles from './userTable.module.less';

type OnImpersonationCallback = (path: string) => void;
type OnUserCallback = (user: UserDetail) => void;

/**
 * Props for LinkedInConnectionCell
 */
type LinkedInConnectionCellProps = Readonly<{
	/**
	 * If user is logged out of LinkedIn
	 */
	isLinkedInLoggedOut: boolean;
	/**
	 * Callback for clicking on cell contents
	 */
	onClick: () => void;
}>;

/**
 * Render component for the LinkedInConnection column
 */
function LinkedInConnectionCell({ isLinkedInLoggedOut, onClick }: LinkedInConnectionCellProps) {
	const { token } = AntTheme.useToken();

	if (!isLinkedInLoggedOut) return <CheckSquareOutlined style={{ color: token.colorSuccess }} />;

	return (
		<Typography.Link onClick={onClick} underline>
			<CloseSquareOutlined style={{ color: token.colorError }} /> Reconnect
		</Typography.Link>
	);
}

function toPermissionDisplayName(role: IOrganizationMemberRole | undefined): string {
	switch (role) {
		case 'ADMIN':
			return 'Admin';
		case 'ADVANCED':
			return 'Advanced';
		case 'BASIC':
			return 'Basic';
		case undefined:
			return '';
		default:
			return '';
	}
}

const primaryContactTagColor = 'rgba(0, 0, 0, 0.15)';

type UserTableProps = Readonly<{
	/**
	 * The users we want to show
	 */
	data: readonly UserDetail[];
	/**
	 * Handler for when impersonation is triggered
	 */
	onImpersonation: OnImpersonationCallback;
	/**
	 * Deactivate the user
	 */
	onDeactivate: OnUserCallback;
	/**
	 * Activate the user
	 */
	onActivate: OnUserCallback;
	/**
	 * Delete the user
	 */
	onDelete?: OnUserCallback;
}>;

/**
 * Table to show a list of users for an account
 * @param props
 * @returns
 */
export function UserTable({
	data,
	onImpersonation,
	onDeactivate,
	onActivate,
	onDelete,
}: UserTableProps) {
	/**
	 * Helper function to get user actions to display for each ro
	 * @param user the details about the user
	 */
	function getUserActions(user: UserDetail) {
		const activateAction = user.isActive ? () => onDeactivate(user) : () => onActivate(user);
		const activateLabel = user.isActive ? 'Deactivate user' : 'Reactivate user';
		return [
			{
				label: 'Edit user',
				onAction: () => drawerManager.openUserEditDrawer({ user }),
			},
			{
				label: activateLabel,
				onAction: data.length > 1 ? activateAction : undefined,
			},
			// TODO COPILOT-7390
			// {
			// 	label: 'Make primary contact',
			// 	onAction: () => console.log('Not Implemented'),
			// },
			...(onDelete
				? [
						{
							label: 'Delete user',
							onAction: data.length > 1 ? () => onDelete(user) : undefined,
							isDanger: true,
						},
				  ]
				: []),
		];
	}
	return (
		<TableModuleTable<UserDetail> rowKey={(row) => row.id} pagination={false} dataSource={data}>
			<TableModuleColumn<UserDetail>
				title="Name"
				columnKey="name"
				ellipsis
				fixed="left"
				className={styles.userNameCol}
				render={(_value, record) => (
					<>
						<Typography.Text ellipsis>
							{`${record.firstName} ${record.lastName}`}
						</Typography.Text>
						{record.isPrimaryContact && (
							<Tag style={{ background: primaryContactTagColor }}>
								Primary Contact
							</Tag>
						)}
					</>
				)}
			/>
			<TableModuleColumn<UserDetail>
				title="LinkedIn status"
				dataIndex="isLinkedInLoggedOut"
				columnKey="isLinkedInLoggedOut"
				render={(isLinkedInLoggedOut: UserDetail['isLinkedInLoggedOut']) => (
					<LinkedInConnectionCell
						isLinkedInLoggedOut={isLinkedInLoggedOut}
						onClick={() => onImpersonation('/?tab=teammembers')}
					/>
				)}
			/>
			<TableModuleColumn<UserDetail>
				title="Status"
				dataIndex="isActive"
				columnKey="isActive"
				render={(isActive: UserDetail['isActive']) => (
					<ActiveStatus status={isActive ? 'Active' : 'Deactivated'} />
				)}
			/>
			<TableModuleColumn<UserDetail>
				title="Permission"
				dataIndex="role"
				columnKey="role"
				render={(value: UserDetail['role']) => (
					<Typography.Text>{toPermissionDisplayName(value)}</Typography.Text>
				)}
			/>
			<TableModuleColumn<UserDetail>
				columnKey="col_actions"
				align="right"
				fixed="right"
				width="50px"
				render={(_v, user) => <Actions actions={getUserActions(user)} />}
			/>
		</TableModuleTable>
	);
}
